/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import bnd3Service from '../../../services/bnd3.service';
import utilityService from '../../../services/utility.service';
import ChartConst from '../../../constants/chart.constant';
import AppConst from '../../../constants/app.constant';
import DefConst from '../../../constants/def.constant';
import bnChartDataProcessService from '../../../services/chartDataProcess.service';
import d3 from '../../../globalexports/d3.export';
import { validateAloftData } from '../../../services/validation/validateAloftData';

function ceilingChartService() {
  const content = {};
  content.drawChart = (
    sourceData,
    settings,
    chartData,
    chartObj,
    chartHeight,
    width,
    topSpace,
    ceilingMaxValueAllowed,
    ceilingMinValueAllowed,
    linearScaleTicks,
    x,
    y1,
    yAxisRectWidth,
    yAxisRightRectWidth,
    aloftData,
    fullData,
  ) => {
    let visBars;
    let visStrip;
    let visTemp;
    let heightOfCloud;
    const cloudsHeightObject = [];
    let cloudIndex;
    let aloftGridContainer;
    let aloftTmpContainer;
    let tmpDataLowerBoundPath;
    let gridColumns;
    let aloftRefDate;
    let airGairSigmetContainer;
    let airSigmetContainer;
    let squareWidth;
    let airmetTrends;
    let elevationHeight;
    if (validateAloftData(aloftData)) {
      aloftRefDate = aloftData.model_ts;
      squareWidth = (width - (yAxisRectWidth * 2)) / aloftData.length;
    }
    if (!sourceData) {
      return null;
    }
    const data = sourceData.forecast;
    // Y-Axis left side ChartConst
    const yAxisLeft = d3.axisRight()
      .scale(y1)
      .tickValues(settings.useLogarithmicScaleForCeiling
        ? ChartConst.logarithmicScaleTicks
        : linearScaleTicks)
      .tickFormat((d) => utilityService.abbreviateCeiling(parseInt(d, 10), ChartConst
        .ChartLabelSpecs.cig.tooltip.mobileUnit))
      .tickSizeInner(-width) // To show horizontal gridlines
      .tickSizeOuter(0)
      .tickPadding(ChartConst.GraphConfig.yAxis.tickPadding);

    // yAxisLeftMinor is for drawing ticks line inside chart
    const yAxisLeftMinor = d3.axisLeft()
      .scale(y1)
      .tickValues(settings.useLogarithmicScaleForCeiling
        ? ChartConst.logarithmicScaleTicks
        : linearScaleTicks)
      .tickFormat('')
      .tickSizeInner(-width) // To show horizontal gridlines
      .tickSizeOuter(0)
      .tickPadding(ChartConst.GraphConfig.yAxis.tickPadding);

    const aloftTempLine = d3.line()
      .curve(ChartConst.bnd3Curve)
      .defined((d) => d.y !== null)
      .x((d) => x(d.x))
      .y((d) => {
        let yValue = d.y;
        if (yValue === ceilingMaxValueAllowed) {
          yValue = ceilingMaxValueAllowed + AppConst.extendedCeiling.min;
        } else if (yValue === AppConst.extendedCeiling.min) {
          // Adding 0.1 to make sure not breaks for log(0) i.e. infinity
          yValue = (AppConst.extendedCeiling.min - AppConst.extendedCeiling.min) + 0.1;
        }
        return y1(yValue);
      });
    // Setting Domain for X-axis
    x.domain(d3.extent(data, (d) => d.forecast_ts));

    y1.domain([(ceilingMinValueAllowed + ChartConst.ceilingDomain.minOffset),
      ceilingMaxValueAllowed + ChartConst.ceilingDomain.maxOffset,
    ]);

    // Drawing clear rect to seperate two charts
    chartObj.append('rect')
      .attr('class', 'clear-rect')
      .attr('x', -1)
      .attr('y', (chartHeight))
      .attr('width', width + 2)
      .attr('height', topSpace);

    chartObj.append('g')
      .attr('class', 'y axis y-left y-left-tick-highlighter')
      .call(yAxisLeftMinor);
    // Drawing Y-axis left
    chartObj.append('g')
      .attr('class', 'y axis y-left y-left-negative-lines')
      .call(yAxisLeft);

    chartObj.append('clipPath')
      .attr('id', 'clip')
      .append('rect')
      .attr('x', yAxisRectWidth)
      .attr('width', width - (yAxisRectWidth + yAxisRightRectWidth))
      .attr('height', chartHeight);
    /* Ceiling using null for 12000ft */
    if (chartData && chartData.length === 0) {
      return chartData;
    }
    const ceilingRangeData = bnChartDataProcessService.processCeilingRangeData(
      utilityService.clone([
        bnChartDataProcessService.getSeriesByKey(chartData, ChartConst.ChartLabelSpecs.cig
          .key),
      ]),
    );

    // old: [ 100, 1000, 1750, 2500, 3250, 4750, 6400, 8000, 9750, 11750, 12000 //]
    // new: 500, 1375, 2125, 2875, 4000, 5575, 7200, 8875, 10750, 12000
    cloudIndex = 0;
    if (validateAloftData(aloftData) && aloftData.viewRelevantData) {
      while (cloudIndex < DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode].length) {
        heightOfCloud = y1(DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode][
          cloudIndex
        ]) - y1(Math.min(ceilingMaxValueAllowed, DefConst[DefConst
          .aloftCloudIndexMapKey + settings.dataMode][cloudIndex + 1]));
        cloudsHeightObject.push(heightOfCloud);
        cloudIndex += 1;
      }
      squareWidth = x(utilityService.addHours(new Date(aloftRefDate), 1)) - x(utilityService
        .addHours(new Date(aloftRefDate), 0));
      aloftGridContainer = chartObj.append('g')
        .attr('class', 'aloftGridContainer');
      gridColumns = aloftGridContainer.selectAll('col')
        .data(aloftData.viewRelevantData)
        .enter()
        .append('g')
        .attr('class', (...args) => {
          const i = args[1];
          return `col col-${i + 1}`;
        })
        .attr('data', (d) => d.model_ts)
        .attr('valueidx', (...args) => {
          const key = args[1];
          return key;
        })
        .attr('transform', (d, key) => {
          let xValue = x(d.model_ts); // DAB shift one column to the right
          if (key !== 0 && xValue !== -100) {
            xValue -= (squareWidth / 2);
          }
          if (xValue < yAxisRectWidth || (xValue >= (width - yAxisRightRectWidth))) {
            xValue = -100; // this will throw box out of the view
          }
          d.cloudBlockXValue = xValue;
          return `translate(${xValue}, 0)`;
        })
        .attr('cloudBlockXValue', (d) => d.cloudBlockXValue)
        .style('display', (d) => {
          const xValue = d.cloudBlockXValue; // DAB shift one column to the right
          if (xValue === -100) {
            return 'none';
          }
          return 'inherit';
        });

      gridColumns.selectAll('rect .row')
        .data((d) => d.altitudes.filter((p, key) => {
          // Filter out values where CIG is above max allowed CIG
          const idx = key;
          p.altitudeIdx = key;
          if (!p.rh || p.rh < ChartConst.cloudThreshold.level1.threshold) {
            p.markedTransparent = true;
          }
          return ((!p.markedTransparent) && (DefConst[DefConst.aloftCloudIndexMapKey
            + settings.dataMode][idx] >= ceilingMinValueAllowed) && (DefConst[DefConst
            .aloftCloudIndexMapKey + settings.dataMode][idx] <= ceilingMaxValueAllowed));
        }))
        .enter()
        .append('rect')
        .style('fill', (...args) => {
          const d = args[0];
          const selfEle = utilityService.getSelfElement(args);
          const cloudBlockXValue = parseInt(d3.select(selfEle.parentNode).attr(
            'cloudBlockXValue',
          ), 10);
          d.cloudBlockXValue = cloudBlockXValue;
          if (d.rh > ChartConst.cloudThreshold.level1.threshold && d.rh < ChartConst
            .cloudThreshold.level2.threshold) {
            return ChartConst.cloudThreshold.level1.color;
          }
          return ChartConst.cloudThreshold.level2.color;
        })
        .attr('class', 'aloft-clouds')
        .attr('height', (d) => {
          const key = d.altitudeIdx;
          let rectHeight = cloudsHeightObject[key];
          const yValue = (y1(DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode][
            key
          ]) - cloudsHeightObject[key]);
          if (yValue + cloudsHeightObject[key] > chartHeight) {
            rectHeight -= ((yValue + cloudsHeightObject[key]) - chartHeight);
          }
          rectHeight = Math.max(rectHeight, 0);
          d.cloudBlockHeight = rectHeight;
          return rectHeight;
        })
        .attr('width', (...args) => {
          const selfEle = utilityService.getSelfElement(args);
          const valueidx = parseInt(d3.select(selfEle.parentNode).attr('valueidx'), 10);
          if (valueidx && (valueidx === 0 || (valueidx === aloftData.viewRelevantData.length
            - 1))) {
            return squareWidth / 2;
          }
          return squareWidth;
        })
        .attr('transform', (d) => {
          const key = d.altitudeIdx;
          const cloudBlockYValue = (y1(DefConst[DefConst.aloftCloudIndexMapKey + settings
            .dataMode][key]) - cloudsHeightObject[key]);
          d.cloudBlockYValue = cloudBlockYValue;
          return `translate(0, ${cloudBlockYValue})`;
        });
    }

    function drawAirmetComponents() {
      // Draw airmet/gairmet only in case of 1-Day view
      airGairSigmetContainer = chartObj.append('g')
        .attr('id', 'meteo-air-gair-sigmet-container');
      airSigmetContainer = airGairSigmetContainer.append('g')
        .attr('id', 'meteo-airsigmet-container');
      airGairSigmetContainer.append('g')
        .attr('id', 'meteo-gairsigmet-container');
      let airmetDataCollection = [];
      if (fullData && fullData.gairmet && fullData.gairmet.gairmet) {
        airmetDataCollection = bnd3Service.calcAirmetPositioningParameters(
          fullData.gairmet
            .gairmet,
          'valid_time_local',
          'expire_time_local',
          'altitude_min',
          'altitude_max',
          x,
          y1,
          chartData[0].values[0].x,
          'gairmet',
          airmetDataCollection,
          fullData.waitForAloft,
          aloftData,
          settings,
          tmpDataLowerBoundPath,
        );
      }
      if (fullData && fullData.airsigmet && fullData.airsigmet.airsigmet) {
        airmetDataCollection = bnd3Service.calcAirmetPositioningParameters(
          fullData.airsigmet
            .airsigmet,
          'valid_time_from_local',
          'valid_time_to_local',
          'altitude_min',
          'altitude_max',
          x,
          y1,
          chartData[0].values[0].x,
          'airsigmet',
          airmetDataCollection,
          fullData.waitForAloft,
          aloftData,
          settings,
          tmpDataLowerBoundPath,
        );
      }
      airmetDataCollection = bnd3Service.filterContainedRect(utilityService.clone(
        airmetDataCollection,
      ));
      bnd3Service.drawAirmetAndGairmet(airSigmetContainer, airmetDataCollection);
      const airmetTrendsTemp = bnChartDataProcessService.getAirmetTrendlineObject(
        airmetDataCollection,
        chartData[0],
        null,
        null,
        settings,
      );
      airmetTrends = airmetTrendsTemp;
    }

    function drawCeilingBars() {
      if (settings.enableCloudBelowCig === false && ceilingRangeData && ceilingRangeData[0]) {
        // now add titles to the axes
        chartObj.append('g')
          .attr('id', 'ceiling-bars-meteo')
          .selectAll('.series')
          .data(ceilingRangeData[0].values)
          .enter()
          .append('rect')
          .attr('clip-path', 'url(#clip)')
          .attr('class', 'ceiling-area-bars crispedges')
          .style('display', (d) => {
            if (d.y >= ChartConst.lastCigValue) {
              return 'none';
            }
            return 'inherit';
          })
          .attr('x', (d, key) => {
            const positionObj = bnd3Service.getNextCurrentPrevX(
              ceilingRangeData[0].values,
              x,
              d,
              'x',
              key,
            );
            return (positionObj.currentX - ((positionObj.currentX - positionObj.prevX) / 2));
          })
          .attr('y', (d) => {
            d.yTransformValue = y1(d.y) - (ChartConst.cigCloudBlockHeight + 1);
            return d.yTransformValue;
          })
          .attr('width', (d, key) => {
            if (d.y === null) {
              // if cig is null then dont draw it i.e. width 0
              return 0;
            }
            const positionObj = bnd3Service.getNextCurrentPrevX(
              ceilingRangeData[0].values,
              x,
              d,
              'x',
              key,
            );
            return ((positionObj.currentX - positionObj.prevX) / 2) + ((positionObj.nextX
              - positionObj.currentX) / 2);
          })
          .attr('height', (d) => Math.max(0, chartHeight - d.yTransformValue));
      }
    }
    drawCeilingBars();
    // Drawing Y-axis left tick lines
    chartObj.append('g')
      .attr('class', 'y axis y-left y-grid-line-container')
      .call(yAxisLeftMinor);
    chartObj.append('g')
      .attr('id', 'cloud-cig')
      .selectAll('scatter-dots')
      .data(ceilingRangeData[0].values)
      .enter()
      .append('rect')
      .attr('clip-path', 'url(#clip)')
      .attr('class', 'crispedges')
      .attr('stroke', '#fff')
      .attr('fill', '#fff')
      .style('display', (d) => {
        if (d.y >= ChartConst.lastCigValue) {
          return 'none';
        }
        return 'inherit';
      })
      .attr('x', (d, key) => {
        const positionObj = bnd3Service.getNextCurrentPrevX(
          ceilingRangeData[0].values,
          x,
          d,
          'x',
          key,
        );
        return (positionObj.currentX - ((positionObj.currentX - positionObj.prevX) / 2));
      })
      .attr('y', (d) => y1(d.y) - (ChartConst.cigCloudBlockHeight + 1))
      .attr('width', (d, key) => {
        if (d.y === null) {
          // if cig is null then dont draw it i.e. width 0
          return 0;
        }
        const positionObj = bnd3Service.getNextCurrentPrevX(
          ceilingRangeData[0].values,
          x,
          d,
          'x',
          key,
        );
        return ((positionObj.currentX - positionObj.prevX) / 2) + ((positionObj.nextX
          - positionObj.currentX) / 2);
      })
      .attr('height', ChartConst.cigCloudBlockHeight);

    function drawFreezingLines() {
      if (validateAloftData(aloftData) && aloftData.freezingLineData) {
        aloftData.freezingLineData.tmpDataUpperBound = bnChartDataProcessService
          .normalizeSecondaryRecord(aloftData.freezingLineData.tmpDataUpperBound, aloftData
            .freezingLineData.tmpDataLowerBound, settings);
        if (bnChartDataProcessService.checkIfAllValueNotAtCeiling(aloftData.freezingLineData
          .tmpDataUpperBound, aloftData.freezingLineData.tmpDataLowerBound, settings)) {
          // Checking if all values of lower/upper freeze value are not at max ceiling
          aloftTmpContainer = chartObj.append('g')
            .attr('class', 'aloftTmpContainer');

          tmpDataLowerBoundPath = aloftTmpContainer.append('path')
            .datum(aloftData.freezingLineData.tmpDataLowerBound)
            .style('stroke-dasharray', ('3, 3'))
            .attr('clip-path', 'url(#clip)')
            .attr('fill', 'none')
            .attr('id', 'tmpDataLowerBoundPath')
            .attr('stroke', ChartConst.ChartLabelSpecs.cigTmp.color)
            .attr('stroke-width', ChartConst.ChartLabelSpecs.cigTmp.strokeWidth)
            .attr('d', aloftTempLine);
          aloftTmpContainer.append('path')
            .datum(aloftData.freezingLineData.tmpDataUpperBound)
            .style('stroke-dasharray', ('3, 3'))
            .attr('clip-path', 'url(#clip)')
            .attr('fill', 'none')
            .attr('stroke', ChartConst.ChartLabelSpecs.cigTmp.color)
            .attr('stroke-width', ChartConst.ChartLabelSpecs.cigTmp.strokeWidth)
            .attr('d', aloftTempLine);

          const cigTmpSeries = utilityService.clone(ChartConst.ChartLabelSpecs.cigTmp, true);
          cigTmpSeries.values = aloftData.freezingLineData.tmpDataLowerBound;
          const cigTmpSeriesSecondary = utilityService.clone(
            ChartConst.ChartLabelSpecs.cigTmp,
            true,
          );
          cigTmpSeriesSecondary.values = aloftData.freezingLineData.tmpDataUpperBound;
          cigTmpSeriesSecondary.secondary = true;
          chartData.push(cigTmpSeries);
          chartData.push(cigTmpSeriesSecondary);
        }
      }
    }

    function drawElevation() {
      elevationHeight = Math.max(chartHeight - y1(settings.airportDetails.el), 1);
    }

    function drawVisibilitybars() {
      // Drawing VISIBILITY bars
      // VISIBILITY bar container
      visBars = chartObj.append('g')
        .attr('id', 'vis-bars-container-meteo');
      visStrip = visBars.selectAll('#vis-bars')
        .data(sourceData.forecast)
        .enter();
      visTemp = visStrip.append('g')
        .attr('id', (...args) => {
          const key = args[1];
          return `vis-temp-${key}`;
        }).attr('transform', (d, key) => {
          const positionObj = bnd3Service.getNextCurrentPrevX(
            sourceData.forecast,
            x,
            d,
            'forecast_ts',
            key,
          );
          return `translate(${positionObj.currentX - ((positionObj.currentX - positionObj.prevX) / 2)},  0)`;
        });
      // If vis is null then use "7" as opacity 0
      visTemp.append('rect')
        .attr('fill', (d) => ChartConst.shadesForVisibility[d.vis || '7'])
        .attr('width', (d, key) => {
          const positionObj = bnd3Service.getNextCurrentPrevX(
            sourceData.forecast,
            x,
            d,
            'forecast_ts',
            key,
          );
          return ((positionObj.currentX - positionObj.prevX) / 2) + ((positionObj.nextX
            - positionObj.currentX) / 2) + 0.1;
        })
        .attr('height', () => {
          const visHeight = Math.max(chartHeight - y1(ceilingMinValueAllowed + ChartConst
            .visibilityTillWhatAltitude), 1);
          return visHeight;
        })
        .attr('transform', () => {
          const yValue = y1(ceilingMinValueAllowed + ChartConst.visibilityTillWhatAltitude)
            - elevationHeight;
          return `translate(0, ${yValue})`;
        });
    }
    if (validateAloftData(aloftData)) {
      drawElevation();
      drawVisibilitybars();
    }
    drawFreezingLines();
    // && (settings.product === ChartConst.products.twelvehours.name
    // || settings.product === ChartConst.products.lamp.name)
    if (!fullData.waitForAloft) {
      drawAirmetComponents();
    } else if (fullData.waitForAloft && aloftData) {
      drawAirmetComponents();
    }

    if (airmetTrends) {
      _forEach(airmetTrends, (item) => {
        chartData.push(item);
      });
    }
    return chartData;
  };
  return content;
}
export default ceilingChartService();
